import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import GridLayout from 'react-grid-layout';
import { WIDGET_CATALOG } from './WidgetLibrary';

interface DraggableWidgetGridProps {
  activeWidgets: string[];
  onUpdateWidgets: (widgets: string[]) => void;
  widgetData: any;
  onWidgetClick?: (widgetId: string, tab: string, filter?: any) => void;
}

export function DraggableWidgetGrid({
  activeWidgets,
  onUpdateWidgets,
  widgetData,
  onWidgetClick
}: DraggableWidgetGridProps) {
  const { t, i18n } = useTranslation();
  const [showDrawer, setShowDrawer] = useState(false);
  const [containerWidth, setContainerWidth] = useState(1200);
  const isRTL = i18n.language === 'he';

  // Define widget navigation - which tab and filter each widget should link to
  const widgetNavigation: { [key: string]: { tab: string, filter?: any } } = {
    // Patients-related widgets
    'patients-overview': { tab: 'projects' },
    'total-patients': { tab: 'projects' },
    'active-patients': { tab: 'projects', filter: { patientStatusFilter: 'active' } },
    'new-patients': { tab: 'projects', filter: { patientStatusFilter: 'new' } },
    'inactive-cases': { tab: 'projects', filter: { patientStatusFilter: 'inactive' } },
    'inactive-patients-list': { tab: 'projects', filter: { patientStatusFilter: 'inactive' } },
    'recently-updated': { tab: 'projects' },
    
    // Events-related widgets
    'events-overview': { tab: 'calendar' },
    'total-events': { tab: 'calendar' },
    'upcoming-events': { tab: 'calendar', filter: { showUpcoming: true } },
    
    // Tasks-related widgets - now go to tasks tab
    'tasks-overview': { tab: 'tasks' },
    'total-tasks': { tab: 'tasks' },
    'pending-tasks': { tab: 'tasks', filter: { taskFilter: 'pending' } },
    'today-tasks': { tab: 'tasks', filter: { taskFilter: 'today' } },
    'pending-tasks-list': { tab: 'tasks', filter: { taskFilter: 'pending' } },
    
    // Completion-related widgets
    'completion-rate': { tab: 'dashboard' },
    'treatment-completion': { tab: 'dashboard' },
    
    // Other widgets
    'recent-activity': { tab: 'dashboard' },
    'notifications-list': { tab: 'dashboard' },
    'today-milestones': { tab: 'dashboard' },
  };

  // Handle widget click
  const handleWidgetClick = (widgetId: string) => {
    const navigation = widgetNavigation[widgetId];
    if (!navigation) return;
    
    // Navigate to tab with filter
    if (onWidgetClick) {
      onWidgetClick(widgetId, navigation.tab, navigation.filter);
    }
  };

  
  // Calculate how many widgets can fit per row based on container width
  const calculateWidgetsPerRow = (containerWidth: number) => {
    // Container width already accounts for padding (20px on each side)
    // Grid margin between widgets is 20px (from margin={[20, 20]})
    // Formula: containerWidth = n * widgetWidth + (n - 1) * 20
    
    // Target widget width (in pixels) - keep widgets at good readable size
    // This ensures widgets don't get too small
    const targetWidgetWidth = 160; // Good size for widgets to remain readable
    
    // Calculate how many widgets can fit with target width
    // availableWidth = n * targetWidgetWidth + (n - 1) * 20
    // n = (containerWidth + 20) / (targetWidgetWidth + 20)
    const widgetsWithTargetWidth = Math.floor((containerWidth + 20) / (targetWidgetWidth + 20));
    
    // Ensure at least 3 widgets per row, allow up to 10 widgets
    // This uses available space while keeping widgets at good size
    return Math.max(3, Math.min(widgetsWithTargetWidth, 10));
  };

  // Helper function to generate a fresh layout with dynamic widgets per row
  const generateFreshLayout = (widgets: string[]) => {
    let currentY = 0;
    let currentX = 0;
    
    // Calculate widgets per row based on container width
    const widgetsPerRow = calculateWidgetsPerRow(containerWidth);
    
    // Filter out ai-assistant and sort: small widgets first, then wide widgets
    const widgetsForLayout = widgets.filter(id => id !== 'ai-assistant');
    const sortedWidgets = [...widgetsForLayout].sort((a, b) => {
      const widgetA = WIDGET_CATALOG.find(w => w.id === a);
      const widgetB = WIDGET_CATALOG.find(w => w.id === b);
      const isWideA = widgetA?.isWide || false;
      const isWideB = widgetB?.isWide || false;
      if (isWideA === isWideB) return 0;
      return isWideA ? 1 : -1;
    });
    
    // Calculate count of small widgets for alignment
    const smallWidgetsCount = sortedWidgets.filter(id => {
      const w = WIDGET_CATALOG.find(cat => cat.id === id);
      return !w?.isWide;
    }).length;

    const generatedLayout = sortedWidgets.map((widgetId, index) => {
      const widget = WIDGET_CATALOG.find(w => w.id === widgetId);
      const isWide = widget?.isWide || false;
      
      // Calculate width
      let width = 1;
      if (isWide) {
        if (smallWidgetsCount > 0) {
          // Match width of small widgets row, but keep within reasonable bounds
          // Minimum 4 columns (unless screen is smaller), maximum full width
          const targetWidth = Math.max(4, smallWidgetsCount);
          width = Math.min(widgetsPerRow, targetWidth);
        } else {
          // If no small widgets, take full width
          width = widgetsPerRow;
        }
      }

      // Wide widgets start with height 4, small widgets with height 2
      const height = isWide ? 4 : 2;
      
      // For wide widgets, always start a new row
      if (isWide && currentX !== 0) {
        currentX = 0;
        currentY += 2; // Use base height for spacing
      }
      
      // For small widgets: if adding this widget would exceed available columns, start new row
      if (!isWide) {
        // Check if adding this widget would exceed the row
        if (currentX + width > widgetsPerRow) {
          currentX = 0;
          currentY += 2; // Use base height for spacing
        }
      }
      
      // Calculate x position - mirror for RTL
      const xPosition = isRTL ? (widgetsPerRow - currentX - width) : currentX;
      
      const layoutItem = {
        i: widgetId,
        x: xPosition,
        y: currentY,
        w: width,
        h: height,
        minW: isWide ? 3 : 1,
        maxW: isWide ? widgetsPerRow : 1, // Prevent small widgets from being resized
        minH: isWide ? 4 : 2,
        maxH: isWide ? 20 : 2, // Wide widgets can expand up to 20, small widgets fixed at 2 (minH = maxH prevents resizing)
        isResizable: isWide // Only wide widgets are resizable
      };
      
      // Update position for next widget
      if (isWide) {
        currentY += height; // Use actual height for wide widgets
        currentX = 0;
      } else {
        currentX += width;
        // If row is full, move to next row
        if (currentX >= widgetsPerRow) {
          currentX = 0;
          currentY += 2; // Use base height for small widgets
        }
      }
      
      return layoutItem;
    });
    
    // Always compact to ensure it starts at Y=0
    return generatedLayout;
  };

  // Calculate current columns for the grid
  const currentCols = calculateWidgetsPerRow(containerWidth);

  // Initialize layout - always generate fresh to ensure proper alignment
  const [layout, setLayout] = useState(() => {
    // Always generate fresh layout on initial load to ensure proper 5-per-row alignment
    return generateFreshLayout(activeWidgets);
  });

  // Sync layout with activeWidgets changes - always regenerate to ensure proper alignment
  useEffect(() => {
    const activeWidgetsWithoutAI = activeWidgets.filter(id => id !== 'ai-assistant');
    
    // Always regenerate layout to ensure proper alignment based on container width
    const newLayout = generateFreshLayout(activeWidgets);
    setLayout(newLayout);
    localStorage.setItem('widgetLayout', JSON.stringify(newLayout));
    
    // Old code below - keeping for reference but should not execute
    if (false) {
    
    // Check if there are any new widgets that don't have layout positions
    const newWidgets = activeWidgets.filter(
      widgetId => widgetId !== 'ai-assistant' && !layout.find((item: any) => item.i === widgetId)
    );

    // If we have missing widgets, add them to layout
    if (newWidgets.length > 0) {
      const updatedLayout = [...layout];
      
      newWidgets.forEach(widgetId => {
        // Check if widget is wide
        const widget = WIDGET_CATALOG.find(w => w.id === widgetId);
        const isWide = widget?.isWide || false;
        // For 5 widgets per row: 12 / 5 = 2.4 columns per widget
        const widgetWidth = isWide ? 12 : 2.4;
        // Wide widgets start with height 4, small widgets with height 2
        const widgetHeight = isWide ? 4 : 2;
        
        // Find the next available position - always compact to top
        const existingPositions = updatedLayout.map(item => ({
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h
        }));
        
        // Find max Y position (bottom of all widgets)
        const maxY = existingPositions.length > 0 
          ? Math.max(...existingPositions.map(p => p.y + p.h))
          : 0;
        
        // Calculate next available position - always at top with no gaps
        let newX = 0;
        let newY = 0;
        
        if (existingPositions.length > 0) {
          if (isWide) {
            // Wide widgets always start on a new row at the bottom
            newX = 0;
            newY = maxY;
          } else {
            // Try to find a spot in the last row (top-most available row)
            const lastRowY = Math.max(...existingPositions.map(p => p.y));
            const lastRowItems = existingPositions.filter(p => p.y === lastRowY);
            const lastRowMaxX = Math.max(...lastRowItems.map(p => p.x + p.w));
            
            if (lastRowMaxX + widgetWidth <= 12) {
              // There's space in the current last row - add here (no gap)
              newX = lastRowMaxX;
              newY = lastRowY;
            } else {
              // Start a new row at the bottom (no gap)
              newX = 0;
              newY = maxY;
            }
          }
        }
        
        // Add new widget to layout - will be compacted to top by GridLayout
        const newItem = {
          i: widgetId,
          x: newX,
          y: newY,
          w: widgetWidth,
          h: isWide ? 4 : widgetHeight, // Wide widgets start with height 4
          minW: isWide ? 6 : 2.4,
          maxW: isWide ? 12 : 2.4, // Prevent small widgets from being resized wider
          minH: isWide ? 4 : 2,
          maxH: isWide ? 20 : 2, // Wide widgets can expand up to 20, small widgets fixed at 2 (minH = maxH prevents resizing)
          isResizable: isWide // Only wide widgets are resizable
        };
        updatedLayout.push(newItem);
      });
      
      setLayout(updatedLayout);
    }

    // Remove widgets from layout that are no longer active
    const removedWidgets = layout.filter(
      (item: any) => !activeWidgets.includes(item.i)
    );
    
    if (removedWidgets.length > 0) {
      setLayout(layout.filter((item: any) => activeWidgets.includes(item.i)));
    }
    }
  }, [activeWidgets]);

  // Update container width on window resize (not affected by drawer)
  useEffect(() => {
    const updateWidth = () => {
      // Get the actual parent container
      const mainContent = document.querySelector('.main-content');
      if (mainContent) {
        const mainContentWidth = mainContent.clientWidth;
        // Subtract padding (20px on each side = 40px total)
        const gridPadding = 40;
        const availableWidth = mainContentWidth - gridPadding;
        setContainerWidth(Math.max(availableWidth, 600));
      } else {
        // Fallback calculation
        const windowWidth = window.innerWidth;
        const sidebarWidth = 280;
        const mainContentPadding = 40; // 20px on each side
        const gridPadding = 40; // 20px on each side
        const availableWidth = windowWidth - sidebarWidth - mainContentPadding - gridPadding;
        setContainerWidth(Math.max(availableWidth, 600));
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);
  
  // Regenerate layout when container width changes to adjust widgets per row
  // Use a ref to track previous width to avoid unnecessary regenerations
  const prevWidthRef = useRef(containerWidth);
  
  useEffect(() => {
    // Only regenerate if width changed significantly (more than 50px difference)
    // This prevents unnecessary regenerations on small resize events
    if (Math.abs(containerWidth - prevWidthRef.current) > 50) {
      const newLayout = generateFreshLayout(activeWidgets);
      setLayout(newLayout);
      localStorage.setItem('widgetLayout', JSON.stringify(newLayout));
      prevWidthRef.current = containerWidth;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerWidth]);

  const handleLayoutChange = (newLayout: any) => {
    // Don't save layout changes if we're in the middle of an invalid drag
    if (isDraggingRef.current && !validDragRef.current) {
      return; // Will be reverted in onDragStop
    }
    
    // Don't save layout changes if we're dragging - wait for dragStop to determine if it was a click or drag
    if (isDraggingRef.current && validDragRef.current) {
      // Check if position actually changed significantly
      const originalItem = originalLayoutRef.current;
      if (originalItem) {
        const currentItem = newLayout.find((item: any) => item.i === originalItem.i);
        if (currentItem) {
          const movedX = Math.abs(currentItem.x - originalItem.x);
          const movedY = Math.abs(currentItem.y - originalItem.y);
          // Only save if moved more than 1.5 grid units (actual drag, not click)
          // This is stricter to prevent any accidental movement
          if (movedX > 1.5 || movedY > 1.5) {
            // This is a real drag, save it
            setLayout(newLayout);
            localStorage.setItem('widgetLayout', JSON.stringify(newLayout));
          } else {
            // Small movement - likely a click, don't save and revert immediately
            const itemIndex = newLayout.findIndex((item: any) => item.i === originalItem.i);
            if (itemIndex !== -1) {
              newLayout[itemIndex] = { ...originalItem };
              setLayout([...newLayout]);
            }
          }
          return;
        }
      }
      // If we can't find the item, don't save during drag
      return;
    }
    
    // Only save if not dragging
    setLayout(newLayout);
    localStorage.setItem('widgetLayout', JSON.stringify(newLayout));
  };

  // Track if drag started from handle
  const validDragRef = useRef<boolean>(false);
  const originalLayoutRef = useRef<any>(null);
  const isDraggingRef = useRef<boolean>(false);
  const dragStartTargetRef = useRef<HTMLElement | null>(null);
  const dragStartPositionRef = useRef<{ x: number; y: number } | null>(null);
  const hasMovedRef = useRef<boolean>(false);

  // Track mouse/touch down to capture the initial target
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      dragStartTargetRef.current = target;
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches && e.touches.length > 0) {
        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement;
        dragStartTargetRef.current = element;
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('touchstart', handleTouchStart, true);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('touchstart', handleTouchStart, true);
    };
  }, []);

  const handleDragStart = (layout: any, oldItem: any, newItem: any, placeholder: any, event: MouseEvent | TouchEvent, element: HTMLElement) => {
    isDraggingRef.current = true;
    hasMovedRef.current = false;
    
    // Store the initial mouse/touch position to detect movement
    if (event instanceof MouseEvent) {
      dragStartPositionRef.current = { x: event.clientX, y: event.clientY };
    } else if (event instanceof TouchEvent && event.touches && event.touches.length > 0) {
      const touch = event.touches[0];
      dragStartPositionRef.current = { x: touch.clientX, y: touch.clientY };
    }
    
    // Since draggableHandle is set, onDragStart should only be called when dragging from the handle
    // Default to valid (trust the library), but verify if we can
    let isFromHandle = true;
    
    // Get the target that initiated the drag to verify
    let target: HTMLElement | null = dragStartTargetRef.current;
    
    if (!target) {
      // Fallback: try to get from event
      if (event instanceof MouseEvent) {
        target = event.target as HTMLElement;
      } else if (event instanceof TouchEvent && event.touches && event.touches.length > 0) {
        const touch = event.touches[0];
        target = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement;
      }
    }
    
    // Only mark as invalid if we can clearly see it's NOT from the handle
    if (target) {
      const dragHandle = target.closest('.widget-drag-handle');
      // If target exists and is clearly NOT from handle, mark as invalid
      if (!dragHandle && target.closest('.widget-content')) {
        isFromHandle = false;
      }
    }
    
    // Store original layout position
    originalLayoutRef.current = { ...oldItem };
    
    // Mark as valid or invalid
    validDragRef.current = isFromHandle;
    
    // If drag didn't start from handle, mark visually (will be reverted in onDragStop)
    if (!isFromHandle) {
      const gridItem = element.closest('.react-grid-item');
      if (gridItem) {
        gridItem.classList.add('invalid-drag');
      }
    }
  };

  const handleDrag = (layout: any, oldItem: any, newItem: any, placeholder: any, event: MouseEvent | TouchEvent, element: HTMLElement) => {
    // Track if the widget actually moved (not just a click)
    if (originalLayoutRef.current) {
      const movedX = Math.abs(newItem.x - originalLayoutRef.current.x);
      const movedY = Math.abs(newItem.y - originalLayoutRef.current.y);
      // Consider it moved if it moved more than 0.5 grid units in any direction
      // This helps distinguish between a click (no movement) and a drag (actual movement)
      if (movedX > 0.5 || movedY > 0.5) {
        hasMovedRef.current = true;
      }
    }
  };

  const handleDragStop = (layout: any, oldItem: any, newItem: any, placeholder: any, event: MouseEvent | TouchEvent, element: HTMLElement) => {
    const wasValidDrag = validDragRef.current;
    
    // If drag didn't start from handle, revert to original position
    if (!wasValidDrag && originalLayoutRef.current) {
      // Find the item in layout and revert it
        const itemIndex = layout.findIndex((item: any) => item.i === oldItem.i);
        if (itemIndex !== -1) {
          layout[itemIndex] = { ...originalLayoutRef.current };
          // const compactedLayout = compactLayoutToTop(layout);
          setLayout([...layout]);
          localStorage.setItem('widgetLayout', JSON.stringify(layout));
        }
      
      // Remove invalid drag class
      const gridItem = element.closest('.react-grid-item');
      if (gridItem) {
        gridItem.classList.remove('invalid-drag', 'react-dragging');
      }
    } else if (wasValidDrag && originalLayoutRef.current) {
      // Check if the widget was actually dropped in a different position
      const finalX = newItem.x;
      const finalY = newItem.y;
      const originalX = originalLayoutRef.current.x;
      const originalY = originalLayoutRef.current.y;
      
      // Calculate the distance moved
      const movedX = Math.abs(finalX - originalX);
      const movedY = Math.abs(finalY - originalY);
      
      // Only save if the widget was moved to a significantly different position (more than 1.5 grid units)
      // This ensures that clicking (which might cause tiny movements) doesn't move the widget
      const wasDroppedInNewPosition = movedX > 1.5 || movedY > 1.5;
      
      if (wasDroppedInNewPosition) {
        // Widget was dragged to a new position - save it
        setLayout([...layout]);
        localStorage.setItem('widgetLayout', JSON.stringify(layout));
      } else {
        // Widget was just clicked (not moved to a new position) - ALWAYS revert to original
        const itemIndex = layout.findIndex((item: any) => item.i === oldItem.i);
        if (itemIndex !== -1) {
          // Force revert to exact original position immediately
          layout[itemIndex] = { ...originalLayoutRef.current };
          // Revert immediately without compaction to prevent any movement
          setLayout([...layout]);
          localStorage.setItem('widgetLayout', JSON.stringify(layout));
          
          // Also force update the DOM element position to prevent visual movement
          const gridItem = element.closest('.react-grid-item');
          if (gridItem) {
            gridItem.classList.remove('react-dragging');
          }
        }
      }
    }
    
    // Reset flags
    isDraggingRef.current = false;
    validDragRef.current = false;
    originalLayoutRef.current = null;
    dragStartTargetRef.current = null;
    dragStartPositionRef.current = null;
    hasMovedRef.current = false;
  };


  const handleAddWidget = (widgetId: string) => {
    if (!activeWidgets.includes(widgetId)) {
      const newActiveWidgets = [...activeWidgets, widgetId];
      onUpdateWidgets(newActiveWidgets);
      // Layout will be updated by useEffect
    }
  };

  const handleRemoveWidget = (widgetId: string) => {
    // Prevent removing the required activity widget
    if (widgetId === 'recent-activity') {
      return;
    }
    
    // Count stat widgets (excluding activity)
    const statWidgets = activeWidgets.filter(id => id !== 'recent-activity');
    
    // Prevent removing if we'd have fewer than 4 stat widgets
    if (statWidgets.length <= 4 && statWidgets.includes(widgetId)) {
      return;
    }
    
    const newActiveWidgets = activeWidgets.filter(id => id !== widgetId);
    onUpdateWidgets(newActiveWidgets);
    // Layout will be updated by useEffect
  };

  // Filter widgets based on permissions
  const { canSeeSitemap } = require('../../utils/roleUtils');
  const availableWidgets = WIDGET_CATALOG.filter(
    (w) => {
      // Don't show if already active
      if (activeWidgets.includes(w.id)) return false;
      
      // Filter widgets based on permissions
      if (widgetData.user) {
        // Filter completion-rate widget based on permissions
        if (w.id === 'completion-rate') {
          return canSeeSitemap(widgetData.user);
        }
        
        // Filter geographic-sitemap widget based on permissions
        if (w.id === 'geographic-sitemap' || w.requiresPermission === 'canSeeSitemap') {
          return canSeeSitemap(widgetData.user);
        }
      }
      
      return true;
    }
  );

  return (
    <div style={{ position: 'relative' }}>
      {/* Toggle Drawer Button */}
      {/* Toggle button - only show when drawer is closed */}
      {!showDrawer && (
        <button
          onClick={() => setShowDrawer(true)}
          style={{
            position: 'fixed',
            top: '50%',
            ...(isRTL ? { left: '0' } : { right: '0' }),
            transform: 'translateY(-50%)',
            background: '#007acc',
            color: 'white',
            border: 'none',
            padding: '16px 12px',
            borderRadius: isRTL ? '0 8px 8px 0' : '8px 0 0 8px',
            cursor: 'pointer',
            zIndex: 1001,
            fontSize: '20px',
            boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px'
          }}
          title={t('widgets.drawer.toggle') || 'Open Widget Panel'}
        >
          <span>{isRTL ? '▶' : '◀'}</span>
          <span style={{ fontSize: '10px', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            {t('widgets.drawer.title') || 'Widgets'}
          </span>
        </button>
      )}

      {/* Widget Drawer */}
      <div style={{
        position: 'fixed',
        top: 0,
        ...(isRTL ? { left: 0 } : { right: 0 }),
        height: '100vh',
        width: '320px',
        background: 'white',
        boxShadow: isRTL ? '2px 0 12px rgba(0,0,0,0.15)' : '-2px 0 12px rgba(0,0,0,0.15)',
        transform: showDrawer ? 'translateX(0)' : (isRTL ? 'translateX(-100%)' : 'translateX(100%)'),
        transition: 'none',
        zIndex: 2000,
        overflowY: 'auto',
        padding: '20px',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        {/* Header with title and close button */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '20px',
          paddingBottom: '12px',
          borderBottom: '2px solid #e0e0e0'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#333', fontWeight: '600' }}>
            {t('widgets.drawer.title') || 'Available Widgets'}
          </h2>
          <button
            onClick={() => setShowDrawer(false)}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px 8px',
              lineHeight: '1',
              borderRadius: '4px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f5f5f5';
              e.currentTarget.style.color = '#333';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#666';
            }}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Active Widgets Section */}
        {activeWidgets.length > 0 && (
          <>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#333' }}>
              {t('widgets.drawer.active') || 'Active Widgets'}
            </h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#666' }}>
              {t('widgets.drawer.clickToRemove') || 'Click to remove from dashboard'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
              {activeWidgets.map(widgetId => {
                const widget = WIDGET_CATALOG.find(w => w.id === widgetId);
                if (!widget) return null;
                return (
                  <div
                    key={widget.id}
                    onClick={() => handleRemoveWidget(widget.id)}
                    style={{
                      padding: '12px',
                      background: '#e8f5e9',
                      border: '2px solid #4caf50',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#ffebee';
                      e.currentTarget.style.borderColor = '#f44336';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#e8f5e9';
                      e.currentTarget.style.borderColor = '#4caf50';
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>{widget.icon}</span>
                    <span style={{ fontSize: '13px', fontWeight: '500', flex: 1 }}>{t(widget.nameKey)}</span>
                    <span style={{ fontSize: '16px', opacity: 0.6 }}>×</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Available Widgets Section */}
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#333' }}>
          {t('widgets.drawer.available') || 'Available Widgets'}
        </h3>
        <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#666' }}>
          {t('widgets.drawer.clickToAdd') || 'Click to add to dashboard'}
        </p>

        {availableWidgets.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            color: '#999'
          }}>
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>✅</div>
            <p style={{ fontSize: '13px' }}>{t('widgets.drawer.allAdded') || 'All widgets added!'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {availableWidgets.map(widget => (
              <div
                key={widget.id}
                onClick={() => handleAddWidget(widget.id)}
                style={{
                  padding: '16px',
                  background: '#f8f9fa',
                  border: '2px dashed #007acc',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e6f2ff';
                  e.currentTarget.style.borderColor = '#0056b3';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8f9fa';
                  e.currentTarget.style.borderColor = '#007acc';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '28px', flexShrink: 0 }}>{widget.icon}</span>
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {t(widget.nameKey)}
                  </h4>
                </div>
                <p style={{ 
                  margin: 0, 
                  fontSize: '12px', 
                  color: '#666', 
                  lineHeight: '1.4',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  wordBreak: 'break-word'
                }}>
                  {t(widget.descriptionKey)}
                </p>
                <div style={{
                  marginTop: '8px',
                  fontSize: '11px',
                  color: '#007acc',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>+</span>
                  <span>{t('widgets.drawer.clickToAdd') || 'Click to add'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Backdrop overlay when drawer is open */}
      {showDrawer && (
        <div 
          onClick={() => setShowDrawer(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 999,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Draggable Widget Grid */}
      <div 
        style={{ 
          padding: '20px', // Add padding all around
          direction: 'ltr', // Always LTR for grid positioning
          width: '100%',
          maxWidth: '100%',
          overflow: 'visible',
          boxSizing: 'border-box'
        }}>
        <GridLayout
          className="widget-grid-layout"
          layout={layout}
          onLayoutChange={handleLayoutChange}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragStop={handleDragStop}
          cols={currentCols}
          rowHeight={90}
          width={containerWidth}
          isDraggable={true}
          isResizable={true}
          compactType="vertical"
          preventCollision={false}
          margin={[30, 30]}
          draggableHandle=".widget-drag-handle"
          draggableCancel=".widget-content, .widget-content *, button, a, input, select, textarea"
          resizeHandles={['s']}
          useCSSTransforms={true}
          isBounded={false}
          autoSize={true}
        >
          {activeWidgets.filter(id => id !== 'ai-assistant').map(widgetId => {
            const widget = WIDGET_CATALOG.find(w => w.id === widgetId);
            if (!widget) return null;

            // Check permissions for completion-rate widget
            if (widgetId === 'completion-rate' && widgetData.user) {
              const { canSeeSitemap } = require('../../utils/roleUtils');
              if (!canSeeSitemap(widgetData.user)) {
                return null;
              }
            }

            const WidgetComponent = widget.component;
            const isWide = widget.isWide || false;

            const isClickable = !isWide && widgetNavigation[widgetId];

            return (
              <div
                key={widgetId}
                className={`widget-box widget-box-container ${isWide ? 'widget-wide' : 'widget-small'}`}
                data-widget-type={isWide ? 'wide' : 'small'}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  direction: isRTL ? 'rtl' : 'ltr', // RTL for content only
                  cursor: isClickable ? 'pointer' : 'default'
                }}
                onClick={(e) => {
                  // Only handle click for small widgets that have relationships
                  // Don't trigger if clicking on drag handle or widget content (buttons, links, etc.)
                  const target = e.target as HTMLElement;
                  if (isClickable && 
                      !target.closest('.widget-drag-handle') && 
                      !target.closest('button') && 
                      !target.closest('a') &&
                      !target.closest('input') &&
                      !target.closest('select') &&
                      !target.closest('textarea')) {
                    handleWidgetClick(widgetId);
                  }
                }}
              >
                {/* Small Drag Handle in Corner */}
                <div 
                  className="widget-drag-handle"
                  style={{
                    position: 'absolute',
                    top: '8px',
                    [isRTL ? 'left' : 'right']: '8px',
                    width: '24px',
                    height: '24px',
                    cursor: 'move',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid #e0e0e0',
                    zIndex: 10,
                    opacity: 0.6,
                    transition: 'all 0.2s ease',
                    touchAction: 'none',
                    userSelect: 'none',
                    WebkitUserSelect: 'none'
                  }}
                  onMouseDown={(e) => {
                    // Store that we're starting a potential drag from the handle
                    dragStartTargetRef.current = e.target as HTMLElement;
                  }}
                  onClick={(e) => {
                    // Stop click propagation to prevent unwanted behavior
                    e.stopPropagation();
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.background = 'rgba(248, 249, 250, 0.95)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.6';
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <svg 
                    width="12" 
                    height="12" 
                    viewBox="0 0 12 12" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="2" cy="2" r="1" fill="#6c757d"/>
                    <circle cx="6" cy="2" r="1" fill="#6c757d"/>
                    <circle cx="10" cy="2" r="1" fill="#6c757d"/>
                    <circle cx="2" cy="6" r="1" fill="#6c757d"/>
                    <circle cx="6" cy="6" r="1" fill="#6c757d"/>
                    <circle cx="10" cy="6" r="1" fill="#6c757d"/>
                    <circle cx="2" cy="10" r="1" fill="#6c757d"/>
                    <circle cx="6" cy="10" r="1" fill="#6c757d"/>
                    <circle cx="10" cy="10" r="1" fill="#6c757d"/>
                  </svg>
                </div>
                
                {/* Widget Content */}
                <div 
                  className="widget-content"
                  style={{ 
                    flex: 1,
                    overflow: 'auto', 
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    touchAction: 'auto',
                    pointerEvents: 'auto'
                  }}
                >
                  <WidgetComponent {...widgetData} />
                </div>
              </div>
            );
          })}
        </GridLayout>
      </div>

      {/* AI Assistant - Rendered as fixed floating button if active */}
      {activeWidgets.includes('ai-assistant') && (() => {
        const aiWidget = WIDGET_CATALOG.find(w => w.id === 'ai-assistant');
        if (aiWidget) {
          const AIComponent = aiWidget.component;
          return <AIComponent />;
        }
        return null;
      })()}

      <style>
        {`
          /* Grid layout positioning - always LTR */
          .widget-grid-layout {
            direction: ltr !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Ensure grid container uses full width and starts from left */
          .react-grid-layout {
            position: relative;
            width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          
          /* Widget styling - 20px margins, always top */
          .react-grid-item {
            transition: none;
            cursor: default !important; /* Remove move cursor from entire widget */
            padding: 0 !important;
          }
          
          /* Widget box container should not be draggable */
          .react-grid-item .widget-box-container {
            pointer-events: auto !important;
            user-select: auto !important;
            -webkit-user-select: auto !important;
          }
          
          /* Only allow drag handle to initiate drags */
          .react-grid-item .widget-drag-handle {
            pointer-events: auto !important;
            touch-action: none !important;
            user-select: none !important;
            -webkit-user-select: none !important;
          }
          
          /* Widget content should not trigger drags */
          .react-grid-item .widget-content {
            pointer-events: auto !important;
            touch-action: auto !important;
            user-select: auto !important;
            -webkit-user-select: auto !important;
          }
          
          /* Prevent the entire grid item from being draggable except via handle */
          .react-grid-item:not(:has(.widget-drag-handle:active)) {
            cursor: default !important;
          }
          
          .react-grid-item.react-dragging {
            transition: none;
            z-index: 100;
            opacity: 0.8;
          }
          
          .react-grid-item.invalid-drag {
            opacity: 0.5;
          }
          
          .react-grid-item.react-grid-placeholder {
            background: #007acc;
            opacity: 0.2;
            border-radius: 12px;
          }
          
          .widget-box,
          .widget-box-container {
            width: 100%;
            height: 100%;
            user-select: auto;
            -webkit-user-select: auto;
          }
          
          .widget-box:hover {
            box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
          }
          
          /* Drag handle styling */
          .widget-drag-handle {
            cursor: move !important;
            touch-action: none !important;
            user-select: none !important;
            -webkit-user-select: none !important;
            pointer-events: auto !important;
          }
          
          .widget-drag-handle:active {
            cursor: grabbing !important;
          }
          
          /* Better text wrapping in widgets */
          .widget-box *,
          .widget-box-container * {
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          
          /* Ensure widget box itself cannot initiate drags */
          .react-grid-item .widget-box-container:not(:has(.widget-drag-handle)) {
            pointer-events: auto !important;
          }
        `}
      </style>
    </div>
  );
}

